import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, Check, Video, Users, Clock, ExternalLink, Download, Radio, Lock } from 'lucide-react';
import { toast } from 'sonner';
import Countdown, { useClassCountdown } from './Countdown';
import StepDots from './StepDots';
import { goPro } from '../../lib/checkout';
import { classWhen, fill, googleCalendarUrl, downloadIcs, joinHref, saveSeat } from '../../lib/nextSteps';

function JoinButton({ cls, label, soonLabel, className = '' }) {
  const navigate = useNavigate();
  const href = joinHref(cls);
  const go = () => {
    if (!href) return navigate('/group-classes');
    return href.startsWith('/') ? navigate(href) : window.open(href, '_blank', 'noopener,noreferrer');
  };
  return (
    <button type="button" onClick={go} disabled={!cls.join_open} title={cls.join_open ? '' : soonLabel}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-bold text-sm transition-all ${cls.join_open ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5' : 'bg-white/10 text-slate-300 cursor-not-allowed'} ${className}`}
      data-testid="ns-join-btn" data-open={cls.join_open}>
      <Video size={16} /> {cls.join_open ? label : soonLabel}
    </button>
  );
}

export function CalendarLinks({ cls, dark = true, label }) {
  const a = dark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900';
  return (
    <div className={`flex items-center gap-2.5 sm:gap-3 text-xs font-semibold flex-wrap ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="ns-calendar-links">
      <span className="hidden sm:inline">{label}:</span>
      <a href={googleCalendarUrl(cls)} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 underline-offset-2 hover:underline ${a}`} data-testid="ns-calendar-google"><span className="sm:hidden">Google</span><span className="hidden sm:inline">Google Calendar</span> <ExternalLink size={11} /></a>
      <button type="button" onClick={() => downloadIcs(cls)} className={`inline-flex items-center gap-1 underline-offset-2 hover:underline ${a}`} data-testid="ns-calendar-ics"><Download size={11} /> <span className="sm:hidden">Apple / Outlook</span><span className="hidden sm:inline">Apple / Outlook (.ics)</span></button>
    </div>
  );
}

/** The large "Your group class with Sage" hero: save a seat → countdown + join. */
export default function ClassHeroCard({ cls, step, steps, texts, vars, onChanged }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const ev = cls.event;
  const c = useClassCountdown(ev.start_at);
  const t = texts.class_card;
  const save = async () => {
    setSaving(true);
    try {
      await saveSeat(cls);
      toast.success('Seat saved — the class was added to your calendar file. See you there!');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save your seat'); }
    setSaving(false);
  };
  return (
    <section className="relative overflow-hidden rounded-3xl md:rounded-[28px] bg-slate-900 text-white p-4 sm:p-6 md:p-8 shadow-2xl shadow-slate-900/20" data-testid="ns-hero" data-kind="class" data-mode={cls.live ? 'live' : cls.seat_saved ? 'saved' : 'save'}>
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle at 85% 20%, #2563EB, transparent 55%), radial-gradient(circle at 10% 90%, #f59e0b, transparent 45%)' }} />
      <div className="relative space-y-4 md:space-y-5">
        <StepDots steps={steps} activeKey={step.key} dark />
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 md:gap-6">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-400" data-testid="ns-class-heading">{fill(t.heading, vars)}</p>
            <h2 className="font-[Manrope] text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight mt-1 leading-tight" data-testid="ns-class-title">{ev.title}</h2>
            <p className="text-sm text-slate-300 mt-1 max-w-xl" data-testid="ns-step-title">{fill(step.title, vars)}</p>
            <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs sm:text-sm text-slate-300 mt-3 md:mt-4">
              <span className="inline-flex items-center gap-1.5 font-semibold text-white" data-testid="ns-class-when"><Clock size={14} className="text-amber-400" /> {classWhen(ev.start_at)}</span>
              <span className="inline-flex items-center gap-1.5"><Video size={14} className="text-blue-300" /> {ev.in_app ? 'Live inside CELPIPMax' : ev.location || 'Zoom'} · {ev.duration_minutes} min</span>
              {ev.attendee_count > 0 && <span className="inline-flex items-center gap-1.5" data-testid="ns-class-attendees"><Users size={14} className="text-emerald-300" /> {ev.attendee_count} attending</span>}
            </div>
          </div>
          <div className="shrink-0">
            {cls.live ? (
              <div className="inline-flex items-center gap-2 rounded-full bg-rose-500/15 border border-rose-400/40 px-4 py-2 text-rose-200 text-sm font-bold" data-testid="ns-live-badge"><Radio size={14} className="animate-pulse" /> {t.live_label}</div>
            ) : (
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">{c?.expired ? 'Starting' : 'Starts in'}</p>
                <Countdown iso={ev.start_at} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap pt-1">
          {cls.is_locked ? (
            <>
              <button type="button" onClick={() => goPro(navigate)}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold px-6 py-3 sm:px-7 sm:py-3.5 text-sm sm:text-base shadow-lg shadow-amber-500/25 sm:hover:-translate-y-0.5 transition-colors sm:transition-all"
                data-testid="ns-upgrade-btn">
                <Lock size={16} /> Upgrade to join
              </button>
              <span className="text-xs text-slate-400">This class is for Pro students</span>
            </>
          ) : cls.seat_saved ? (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 px-3 py-1.5 text-xs font-bold" data-testid="ns-seat-saved-badge"><Check size={13} strokeWidth={3} /> {t.saved_label}</span>
              <JoinButton cls={cls} label={t.join_label} soonLabel={t.join_soon_label} />
              <CalendarLinks cls={cls} label={t.calendar_label} />
            </>
          ) : (
            <>
              <button type="button" onClick={save} disabled={saving}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold px-6 py-3 sm:px-7 sm:py-3.5 text-sm sm:text-base shadow-lg shadow-amber-500/25 sm:hover:-translate-y-0.5 transition-colors sm:transition-all disabled:opacity-60"
                data-testid="ns-save-seat-btn">
                <CalendarPlus size={18} /> {saving ? 'Saving…' : fill(step.button_label, vars)}
              </button>
              <span className="text-xs text-slate-400">Free · one click · adds it to your calendar</span>
              <Link to="/group-classes" className="text-xs font-semibold text-slate-300 hover:text-white underline-offset-2 hover:underline ml-auto" data-testid="ns-all-classes-link">All classes →</Link>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
